import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { FULL_MONTHS, thisMonth, thisYear, performanceBand, classNames } from "../lib/utils";
import PerformanceBadge from "../components/PerformanceBadge";
import { Save, Sparkles, Trash2, AlertTriangle } from "lucide-react";
import AISummaryCard from "../components/AISummaryCard";

export default function ScoreEntryPage() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [homes, setHomes] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const hmHomes = (user.assigned_homes && user.assigned_homes.length ? user.assigned_homes : (user.assigned_home ? [user.assigned_home] : []));
  const isMultiHomeHM = user.access_role === "House Manager" && hmHomes.length > 1;
  const [home, setHome] = useState(user.access_role === "House Manager" ? (hmHomes[0] || "") : "");
  const [month, setMonth] = useState(thisMonth());
  const [year, setYear] = useState(thisYear());
  const [staffUserId, setStaffUserId] = useState("");
  const [scores, setScores] = useState({}); // metric_id -> 1..5
  const [writeups, setWriteups] = useState("");
  const [managerNotes, setManagerNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [scoreExists, setScoreExists] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePhrase, setDeletePhrase] = useState("");
  const [deleting, setDeleting] = useState(false);

  const todayISO = () => new Date().toISOString().slice(0, 10);
  const [scoringDate, setScoringDate] = useState(todayISO());
  const canEditDelete = ["CEO", "Project Manager", "Administration", "House Manager"].includes(user.access_role);
  const selectedStaff = staffList.find((s) => s.user_id === staffUserId);

  useEffect(() => {
    api.get("/kpi/catalog").then(({ data }) => setCatalog(data));
    api.get("/homes").then(({ data }) => setHomes(data));
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  useEffect(() => {
    if (!home) { setStaffList([]); return; }
    api.get("/staff", { params: { home } }).then(({ data }) => setStaffList(data));
    setStaffUserId("");
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [home]);

  // Load existing scores when filters change
  const reload = useCallback(() => {
    if (!home || !staffUserId) {
      setScores({}); setWriteups(""); setManagerNotes(""); setScoreExists(false);
      setScoringDate(todayISO());
      return;
    }
    Promise.all([
      api.get("/scores", { params: { home, staff_user_id: staffUserId, month, year } }),
      api.get("/summary", { params: { home, staff_user_id: staffUserId, month, year } }),
    ]).then(([scoresResp, summaryResp]) => {
      const map = {};
      (scoresResp.data || []).forEach((s) => { map[s.metric_id] = s.score; });
      setScores(map);
      setWriteups(scoresResp.data?.[0]?.writeups || "");
      setManagerNotes(summaryResp.data?.[0]?.manager_notes || "");
      setScoreExists((scoresResp.data || []).length > 0 || (summaryResp.data || []).length > 0);
      // Preserve the saved scoring date on edit; never silently bump it to today.
      setScoringDate(summaryResp.data?.[0]?.scoring_date || todayISO());
    });
  }, [home, staffUserId, month, year]);

  useEffect(() => { reload(); }, [reload]);

  const liveSummary = useMemo(() => {
    if (!catalog.length) return null;
    const byCat = {};
    catalog.forEach((c) => { byCat[c.id] = []; });
    catalog.forEach((c) => {
      c.metrics.forEach((m) => {
        const v = scores[m.id];
        if (typeof v === "number" && v >= 1 && v <= 5) byCat[c.id].push(v);
      });
    });
    const cat_avgs = {};
    let overall = 0;
    catalog.forEach((c) => {
      const arr = byCat[c.id];
      const avg = arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
      cat_avgs[c.category_key] = +avg.toFixed(2);
      overall += avg * (c.weight / 100);
    });
    return { cat_avgs, overall: +overall.toFixed(2), band: performanceBand(+overall.toFixed(2)) };
  }, [scores, catalog]);

  const totalMetrics = catalog.reduce((a, c) => a + c.metrics.length, 0);
  const filledMetrics = Object.keys(scores).filter((k) => scores[k] >= 1 && scores[k] <= 5).length;

  const setScore = (metricId, value) => setScores((s) => ({ ...s, [metricId]: value }));

  const handleSave = async () => {
    if (!home || !staffUserId) return toast.error("Select home and caregiver first.");
    if (filledMetrics !== totalMetrics) return toast.error("Please complete all scores before saving.");
    setSaving(true);
    try {
      const { data } = await api.post("/scores", {
        home, staff_user_id: staffUserId, month: +month, year: +year, scores,
        writeups, manager_notes: managerNotes,
        scoring_date: scoringDate,
      });
      toast.success(`${data.is_update ? "Score updated" : "Score saved"} · ${data.summary.overall_weighted_score} ${data.summary.performance_band}`);
      setScoreExists(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const openDelete = () => { setDeletePhrase(""); setDeleteOpen(true); };
  const confirmDelete = async () => {
    if (deletePhrase.trim() !== "DELETE SCORE") {
      return toast.error("Type DELETE SCORE exactly to confirm.");
    }
    setDeleting(true);
    try {
      const { data } = await api.delete("/scores", {
        params: { home, staff_user_id: staffUserId, month: +month, year: +year, confirm: "DELETE SCORE" }
      });
      toast.success(`Score deleted (${data.metric_scores_deleted} metrics cleared)`);
      setDeleteOpen(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6 pb-32">
      <div>
        <p className="text-small-caps">Score Entry</p>
        <h1 className="text-h1 mt-1">Monthly KPI Scoring</h1>
        <p className="text-[15px] text-slate-500 mt-1.5">Score each caregiver on 29 metrics across 6 weighted categories.</p>
      </div>

      <div className="nv-card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="score-entry-filters">
          <div>
            <label className="text-small-caps">Home</label>
            <select value={home} onChange={(e) => setHome(e.target.value)} disabled={user.access_role === "House Manager" && !isMultiHomeHM} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2 disabled:bg-slate-50" data-testid="score-home-select">
              <option value="">Select home…</option>
              {homes.map((h) => <option key={h.home_name} value={h.home_name}>{h.home_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-small-caps">Month</label>
            <select value={month} onChange={(e) => setMonth(+e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="score-month-select">
              {FULL_MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-small-caps">Year</label>
            <input type="number" value={year} onChange={(e) => setYear(+e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="score-year-input" />
          </div>
          <div>
            <label className="text-small-caps">Caregiver</label>
            <select value={staffUserId} onChange={(e) => setStaffUserId(e.target.value)} disabled={!home} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2 disabled:bg-slate-50" data-testid="score-staff-select">
              <option value="">Select caregiver…</option>
              {staffList.map((s) => {
                const title = s.job_title || s.staff_classification || s.role_title || "";
                return <option key={s.user_id} value={s.user_id}>{s.full_name}{title ? ` — ${title}` : ""}</option>;
              })}
            </select>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-3 pt-3 border-t border-slate-100">
          <div className="max-w-[200px]">
            <label className="text-small-caps" title="The actual date you are entering these scores. The selected Month/Year above is the period the scores are FOR.">
              Scoring Date
            </label>
            <input type="date" value={scoringDate} onChange={(e) => setScoringDate(e.target.value)}
              className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2"
              data-testid="score-scoring-date" />
          </div>
          <p className="text-xs text-slate-500 mb-2">
            Scoring <span className="font-semibold text-slate-700">{FULL_MONTHS[month - 1]} {year}</span> performance · entered today by default · adjustable.
          </p>
        </div>
        {scoreExists && staffUserId && (
          <div
            className="mt-3 inline-flex items-center rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-3 py-1 text-xs font-semibold"
            data-testid="score-editing-existing-banner"
          >
            Editing Existing Monthly Score{selectedStaff ? ` · ${selectedStaff.full_name}` : ""} · {FULL_MONTHS[month - 1]} {year}
          </div>
        )}
      </div>

      {liveSummary && (
        <div className="nv-card sticky top-2 z-20 border-2 border-sky-100 bg-gradient-to-br from-sky-50 via-white to-emerald-50/50" data-testid="live-summary-banner">
          <div className="p-5 grid grid-cols-2 lg:grid-cols-8 gap-4 items-center">
            <div className="lg:col-span-2">
              <p className="text-small-caps text-sky-700">Overall Weighted Score</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-4xl font-heading font-bold text-slate-900 tabular-nums" data-testid="live-overall-score">{liveSummary.overall.toFixed(2)}</span>
                <span className="text-sm text-slate-500">/ 5.00</span>
              </div>
              <div className="mt-1.5"><PerformanceBadge band={liveSummary.band} /></div>
              <p className="text-sm text-slate-500 mt-1">{filledMetrics} / {totalMetrics} metrics</p>
            </div>
            {catalog.map((c) => (
              <div key={c.id} className="rounded-lg bg-white/80 border border-slate-200 p-3">
                <p className="text-[12.5px] uppercase tracking-wider font-bold text-slate-500 truncate">{c.category_name}</p>
                <p className="text-2xl font-heading font-bold text-slate-800 tabular-nums">{(liveSummary.cat_avgs[c.category_key] || 0).toFixed(2)}</p>
                <p className="text-[12.5px] text-slate-400">{c.weight}% weight</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="score-entry-cards">
        {catalog.map((cat) => (
          <CategoryCard key={cat.id} cat={cat} scores={scores} setScore={setScore} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="nv-card p-5">
          <h3 className="text-h3">Write-ups This Period</h3>
          <textarea
            value={writeups}
            onChange={(e) => setWriteups(e.target.value)}
            rows={4}
            placeholder="Documentation of any disciplinary or performance write-ups during this period…"
            className="mt-3 w-full text-[15px] leading-relaxed rounded-lg border border-slate-200 bg-white p-3 focus:outline-none focus:ring-2 focus:ring-sky-500/30"
            data-testid="score-writeups-input"
          />
        </div>
        <div className="nv-card p-5">
          <h3 className="text-h3">Manager Notes</h3>
          <textarea
            value={managerNotes}
            onChange={(e) => setManagerNotes(e.target.value)}
            rows={4}
            placeholder="Manager observations, recommendations, or coaching notes…"
            className="mt-3 w-full text-[15px] leading-relaxed rounded-lg border border-slate-200 bg-white p-3 focus:outline-none focus:ring-2 focus:ring-sky-500/30"
            data-testid="score-manager-notes-input"
          />
        </div>
      </div>

      {scoreExists && home && staffUserId && (
        <AISummaryCard
          title="AI Performance Coaching for this caregiver"
          endpoint="/ai/kpi-recommendations"
          payload={{ home, staff_user_id: staffUserId, month: +month, year: +year }}
          testIdPrefix="ai-kpi-recs"
          render={(d) => (
            <div className="space-y-3">
              {d.summary && <p>{d.summary}</p>}
              {/* AI output lists below are append-only (regenerate replaces the whole `d` object), so index is a stable key for that render's lifetime. */}
              {Array.isArray(d.weak_categories) && d.weak_categories.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">Weak categories</p>
                  <ul className="list-disc ml-5 space-y-0.5">{d.weak_categories.map((w, i) => <li key={`weak-${i}`}><strong>{w.category}</strong> ({w.score}) — {w.why}</li>)}</ul>
                </div>
              )}
              {Array.isArray(d.improvement_suggestions) && d.improvement_suggestions.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">Coaching suggestions</p>
                  <ul className="list-disc ml-5 space-y-0.5">{d.improvement_suggestions.map((s, i) => <li key={`sug-${i}`}>{s}</li>)}</ul>
                </div>
              )}
              {Array.isArray(d.recognition) && d.recognition.length > 0 && (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2">
                  <p className="text-xs uppercase tracking-wider font-semibold text-emerald-700 mb-1">Recognize in next 1:1</p>
                  <ul className="list-disc ml-5 space-y-0.5 text-emerald-900">{d.recognition.map((r, i) => <li key={`rec-${i}`}>{r}</li>)}</ul>
                </div>
              )}
            </div>
          )}
        />
      )}

      <div className="fixed bottom-4 inset-x-0 lg:left-64 px-6 z-30 no-print pointer-events-none">
        <div className="max-w-5xl mx-auto pointer-events-auto">
          <div className="rounded-xl bg-slate-900 text-white shadow-2xl border border-slate-800 p-3 flex items-center gap-3">
            <Sparkles size={18} className="text-sky-300" />
            <p className="text-sm">{filledMetrics === totalMetrics ? "All scores complete." : `${totalMetrics - filledMetrics} metric(s) remaining`}</p>
            {scoreExists && canEditDelete && (
              <button
                onClick={openDelete}
                className="inline-flex items-center gap-1.5 bg-rose-700 hover:bg-rose-800 text-white px-3 py-2 rounded-lg text-sm font-semibold"
                data-testid="score-delete-btn"
              >
                <Trash2 size={14} /> Delete Score
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || filledMetrics !== totalMetrics || !home || !staffUserId}
              className="ml-auto inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-white px-4 py-2 rounded-lg text-sm font-semibold"
              data-testid="save-score-btn"
            >
              <Save size={15} /> {saving ? "Saving…" : (scoreExists ? "Update Score" : "Save Score")}
            </button>
          </div>
        </div>
      </div>

      {deleteOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={() => !deleting && setDeleteOpen(false)} data-testid="score-delete-modal">
          <div className="nv-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-rose-700 font-bold">
              <AlertTriangle size={20} />
              <h2 className="text-h2">Delete Monthly KPI Score?</h2>
            </div>
            <p className="mt-3 text-sm text-slate-700">
              This will permanently delete <strong>{selectedStaff?.full_name || "this caregiver"}</strong>'s KPI score for <strong>{FULL_MONTHS[month - 1]} {year}</strong>, including all metric scores, summary values, write-ups, and manager notes. <span className="font-semibold text-rose-700">This action cannot be undone.</span>
            </p>
            <label className="block mt-4">
              <span className="text-small-caps">To confirm, type <code className="bg-slate-100 px-1 rounded text-xs">DELETE SCORE</code></span>
              <input
                type="text"
                value={deletePhrase}
                onChange={(e) => setDeletePhrase(e.target.value)}
                autoFocus
                placeholder="DELETE SCORE"
                className="mt-1.5 w-full text-sm rounded-md border border-slate-200 px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-rose-500/30"
                data-testid="score-delete-phrase-input"
              />
            </label>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setDeleteOpen(false)} disabled={deleting} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50" data-testid="score-delete-cancel">Cancel</button>
              <button
                onClick={confirmDelete}
                disabled={deleting || deletePhrase.trim() !== "DELETE SCORE"}
                className="px-4 py-2 rounded-md text-sm font-semibold bg-rose-700 hover:bg-rose-800 text-white disabled:opacity-50"
                data-testid="score-delete-confirm-btn"
              >
                {deleting ? "Deleting…" : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryCard({ cat, scores, setScore }) {
  return (
    <div className="nv-card p-5" data-testid={`score-category-${cat.category_key}`}>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-h3">{cat.category_name}</h3>
          <p className="text-sm text-slate-500 mt-1">{cat.metrics.length} metrics · weighted at {cat.weight}%</p>
        </div>
        <span className="text-xs font-bold rounded-full bg-sky-50 text-sky-700 px-2.5 py-0.5 border border-sky-200">{cat.weight}%</span>
      </div>
      <div className="mt-5 divide-y divide-slate-100">
        {cat.metrics.map((m) => (
          <div
            key={m.id}
            className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-3 py-3.5 first:pt-1"
            data-testid={`metric-row-${m.id}`}
          >
            <p
              className="text-[15px] leading-snug text-slate-800 font-medium flex-1 min-w-0 break-words"
              title={m.metric_name}
            >
              {m.metric_name}
            </p>
            <div className="flex gap-1.5 shrink-0">
              {[1, 2, 3, 4, 5].map((v) => (
                <button
                  key={v}
                  onClick={() => setScore(m.id, v)}
                  data-testid={`score-btn-${m.id}-${v}`}
                  aria-label={`Score ${v} for ${m.metric_name}`}
                  className={classNames(
                    "h-11 w-11 sm:h-12 sm:w-12 rounded-lg text-base font-bold transition-all border touch-manipulation",
                    scores[m.id] === v
                      ? "bg-sky-700 text-white border-sky-700 shadow-md scale-105"
                      : "bg-white text-slate-700 border-slate-200 hover:border-sky-400 hover:text-sky-700 hover:bg-sky-50/50"
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
